/**
 * API 에러 메시지 → 한국어 사용자 메시지 매핑
 *
 * 서버에서 내려오는 영문 에러 메시지를 사용자에게 보여줄 한국어 메시지로 변환한다.
 * 매핑되지 않는 메시지는 도메인별 기본 메시지로 대체하여
 * 기술적 메시지가 사용자에게 노출되지 않도록 한다.
 */

import type { ApiError } from "@/shared/api/types";

const ERROR_MESSAGE_MAP: Record<string, string> = {
  // Auth
  "Registered User Email": "이미 가입된 이메일입니다",
  "Registered User Registration Number": "이미 등록된 사업자등록번호입니다",
  "Invalid credentials": "이메일 또는 비밀번호가 올바르지 않습니다",
  "Invalid password": "비밀번호가 올바르지 않습니다",
  "Wrong Passwrod": "비밀번호가 올바르지 않습니다",
  "Wrong Password": "비밀번호가 올바르지 않습니다",
  "User not found": "가입되지 않은 이메일입니다",
  Unauthorized: "로그인이 필요합니다",

  // SMS
  "Invalid code": "인증번호가 올바르지 않습니다",
  "Code expired": "인증번호가 만료되었습니다",
  "Too many requests": "요청이 너무 많습니다. 잠시 후 다시 시도해주세요",

  // Network
  "Network error": "네트워크 연결을 확인해주세요",
  "Invalid JSON response": "서버 응답 오류가 발생했습니다",
  "WEBP conversion failed":
    "WEBP 파일을 처리하지 못했습니다. JPG, PNG 또는 PDF로 다시 시도해주세요",
  "확장자 에러": "지원하지 않는 파일 형식입니다. JPG, PNG, WEBP, PDF 또는 HEIC 파일을 선택해주세요",
};

/**
 * API 에러를 사용자에게 보여줄 한국어 메시지로 변환한다.
 *
 * @param error - catch된 에러 (ApiError 또는 Error)
 * @param fallback - 매핑되지 않을 때 보여줄 기본 메시지
 */
export function toUserMessage(error: unknown, fallback: string): string {
  const message = getErrorMessage(error);
  return ERROR_MESSAGE_MAP[message] ?? fallback;
}

function getErrorMessage(error: unknown): string {
  if (typeof error === "object" && error !== null && "message" in error) {
    return (error as ApiError).message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "";
}

/**
 * API 에러에서 원본 서버 메시지를 추출한다.
 * 내부 로직(에러 분기)에서 사용. 사용자에게 직접 노출하지 않는다.
 */
export function getRawMessage(error: unknown): string {
  return getErrorMessage(error);
}
